// src/components/Resume/ResumeForm.jsx
import React, { useState } from 'react';
import { Plus, Trash2, Upload } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabaseClient';
import toast from 'react-hot-toast';

const ResumeForm = ({ 
  formData, 
  setFormData, 
  onSave, 
  industrySections = {}, 
  currentIndustry = 'generic' 
}) => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);

  const industryConfig = industrySections?.[currentIndustry] || industrySections?.generic || { 
    id: 'generic',
    name: 'Generic Resume',
    icon: '📄',
    sections: []
  };
  
  const sections = industryConfig.sections || [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNestedInputChange = (sectionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || {}),
        [field]: value
      }
    }));
  };

  const handleArrayAdd = (field) => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      let newItem;
      
      if (field === 'education') {
        newItem = { degree: '', institution: '', year: '', honors: '' };
      } else if (field === 'experience' || field === 'flight-experience') {
        newItem = { title: '', company: '', startDate: '', endDate: '', description: '' };
      } else if (field === 'certifications') {
        newItem = { name: '', issuer: '', year: '' };
      } else if (field === 'publications') {
        newItem = { title: '', journal: '', year: '', doi: '' };
      } else if (field.includes('projects')) {
        newItem = { projectName: '', description: '', technologies: '', outcome: '' };
      } else if (field === 'skills') {
        newItem = '';
      } else if (field === 'languages') {
        newItem = { name: '', proficiency: '' };
      } else {
        newItem = '';
      }
      
      return { ...prev, [field]: [...currentArray, newItem] };
    });
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      if (index < 0 || index >= currentArray.length) return prev;
      const newArray = currentArray.filter((_, i) => i !== index);
      return { ...prev, [field]: newArray };
    });
  };

  const handleArrayUpdate = (field, index, key, value) => {
    setFormData(prev => {
      const currentArray = prev[field] || [];
      if (index < 0 || index >= currentArray.length) return prev;
      
      const updatedArray = [...currentArray];
      const item = updatedArray[index];
      
      if (typeof item === 'string') {
        if (key === '' || key === 'name') {
          updatedArray[index] = value;
        } else {
          updatedArray[index] = { name: item, [key]: value };
        }
      } else if (typeof item === 'object' && item !== null) {
        updatedArray[index] = { ...item, [key]: value };
      } else {
        updatedArray[index] = { [key]: value };
      }
      
      return { ...prev, [field]: updatedArray };
    });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }

    setUploading(true);
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;
    const filePath = `photos/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('resume-photos')
      .upload(filePath, file);

    if (uploadError) {
      toast.error('Failed to upload photo');
      setUploading(false);
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('resume-photos')
      .getPublicUrl(filePath);

    setFormData(prev => ({ ...prev, photo_url: publicUrl }));
    toast.success('Photo uploaded!');
    setUploading(false);
  };

  // ===== RENDER STRING ARRAY FIELD =====
  const renderStringArrayField = (section) => {
    const value = formData[section.id] || [];
    
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label fw-medium mb-0">
            {section.label} {section.required && <span className="text-danger">*</span>}
          </label>
          <button
            type="button"
            onClick={() => handleArrayAdd(section.id)}
            className="btn btn-link btn-sm text-primary text-decoration-none d-flex align-items-center gap-1 p-0"
          >
            <Plus size={16} /> Add
          </button>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {value.map((item, idx) => (
            <div key={idx} className="d-flex align-items-center bg-light rounded-pill px-3 py-1">
              <input
                value={typeof item === 'string' ? item : item?.name || ''}
                onChange={(e) => handleArrayUpdate(section.id, idx, typeof item === 'string' ? '' : 'name', e.target.value)}
                placeholder={section.placeholder || 'Enter item...'}
                className="border-0 bg-transparent small"
                style={{ outline: 'none', width: '100px' }}
              />
              <button
                type="button"
                onClick={() => handleArrayRemove(section.id, idx)}
                className="btn btn-link btn-sm text-danger p-0 ms-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {value.length === 0 && (
            <span className="text-muted small">No items added</span>
          )}
        </div>
      </div>
    );
  };

  // ===== RENDER SKILLS FIELD =====
  const renderSkillsField = (section) => {
    const value = formData[section.id] || section.defaultSkills || [];
    
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label fw-medium mb-0">
            {section.label} {section.required && <span className="text-danger">*</span>}
          </label>
          <button
            type="button"
            onClick={() => handleArrayAdd(section.id)}
            className="btn btn-link btn-sm text-primary text-decoration-none d-flex align-items-center gap-1 p-0"
          >
            <Plus size={16} /> Add
          </button>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {value.map((item, idx) => (
            <div key={idx} className="d-flex align-items-center bg-light rounded-pill px-3 py-1">
              <input
                value={typeof item === 'string' ? item : item?.name || ''}
                onChange={(e) => {
                  if (typeof item === 'string') {
                    handleArrayUpdate(section.id, idx, '', e.target.value);
                  } else {
                    handleArrayUpdate(section.id, idx, 'name', e.target.value);
                  }
                }}
                placeholder={section.placeholder || 'Add skill...'}
                className="border-0 bg-transparent small"
                style={{ outline: 'none', width: '100px' }}
              />
              <button
                type="button"
                onClick={() => handleArrayRemove(section.id, idx)}
                className="btn btn-link btn-sm text-danger p-0 ms-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {value.length === 0 && (
            <span className="text-muted small">No skills added</span>
          )}
        </div>
      </div>
    );
  };

  // ===== RENDER EDUCATION FIELD =====
  const renderEducationField = () => {
    const value = formData.education || [];
    
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label fw-medium mb-0">Education Entries</label>
          <button
            type="button"
            onClick={() => handleArrayAdd('education')}
            className="btn btn-link btn-sm text-primary text-decoration-none d-flex align-items-center gap-1 p-0"
          >
            <Plus size={16} /> Add Education
          </button>
        </div>
        {value.map((item, idx) => (
          <div key={idx} className="border-top pt-3 mt-3">
            <div className="row g-3">
              <div className="col-md-6">
                <input
                  value={item.degree || ''}
                  onChange={(e) => handleArrayUpdate('education', idx, 'degree', e.target.value)}
                  placeholder="Degree (e.g. MBA, BSc)"
                  className="form-control"
                />
              </div>
              <div className="col-md-6">
                <input
                  value={item.institution || ''}
                  onChange={(e) => handleArrayUpdate('education', idx, 'institution', e.target.value)}
                  placeholder="Institution"
                  className="form-control"
                />
              </div>
              <div className="col-md-6">
                <input
                  value={item.year || ''}
                  onChange={(e) => handleArrayUpdate('education', idx, 'year', e.target.value)}
                  placeholder="Year (e.g. 2020-2024)"
                  className="form-control"
                />
              </div>
              <div className="col-md-6">
                <input
                  value={item.honors || ''}
                  onChange={(e) => handleArrayUpdate('education', idx, 'honors', e.target.value)}
                  placeholder="Honors (optional)"
                  className="form-control"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleArrayRemove('education', idx)}
              className="btn btn-link btn-sm text-danger text-decoration-none d-flex align-items-center gap-1 p-0 mt-2"
            >
              <Trash2 size={16} /> Remove
            </button>
          </div>
        ))}
        {value.length === 0 && (
          <span className="text-muted small">No education entries added</span>
        )}
      </div>
    );
  };

  // ===== RENDER CERTIFICATIONS FIELD =====
  const renderCertificationsField = () => {
    const value = formData.certifications || [];
    
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label fw-medium mb-0">Certifications</label>
          <button
            type="button"
            onClick={() => handleArrayAdd('certifications')}
            className="btn btn-link btn-sm text-primary text-decoration-none d-flex align-items-center gap-1 p-0"
          >
            <Plus size={16} /> Add Certification
          </button>
        </div>
        {value.map((item, idx) => (
          <div key={idx} className="d-flex gap-2 mb-2 align-items-center flex-wrap">
            <input
              value={item.name || ''}
              onChange={(e) => handleArrayUpdate('certifications', idx, 'name', e.target.value)}
              placeholder="Certification Name"
              className="form-control"
              style={{ flex: 1, minWidth: '150px' }}
            />
            <input
              value={item.issuer || ''}
              onChange={(e) => handleArrayUpdate('certifications', idx, 'issuer', e.target.value)}
              placeholder="Issuer"
              className="form-control"
              style={{ flex: 1, minWidth: '120px' }}
            />
            <input
              value={item.year || ''}
              onChange={(e) => handleArrayUpdate('certifications', idx, 'year', e.target.value)}
              placeholder="Year"
              className="form-control"
              style={{ width: '100px' }}
            />
            <button
              type="button"
              onClick={() => handleArrayRemove('certifications', idx)}
              className="btn btn-link text-danger p-1"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {value.length === 0 && (
          <span className="text-muted small">No certifications added</span>
        )}
      </div>
    );
  };

  // ===== RENDER LANGUAGES FIELD =====
  const renderLanguagesField = () => {
    const value = formData.languages || [];
    
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label fw-medium mb-0">Languages</label>
          <button
            type="button"
            onClick={() => handleArrayAdd('languages')}
            className="btn btn-link btn-sm text-primary text-decoration-none d-flex align-items-center gap-1 p-0"
          >
            <Plus size={16} /> Add Language
          </button>
        </div>
        {value.map((item, idx) => (
          <div key={idx} className="d-flex gap-2 mb-2 align-items-center">
            <input
              value={item.name || ''}
              onChange={(e) => handleArrayUpdate('languages', idx, 'name', e.target.value)}
              placeholder="Language"
              className="form-control flex-grow-1"
            />
            <input
              value={item.proficiency || ''}
              onChange={(e) => handleArrayUpdate('languages', idx, 'proficiency', e.target.value)}
              placeholder="Proficiency (Native, Fluent, etc.)"
              className="form-control flex-grow-1"
            />
            <button
              type="button"
              onClick={() => handleArrayRemove('languages', idx)}
              className="btn btn-link text-danger p-1"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {value.length === 0 && (
          <span className="text-muted small">No languages added</span>
        )}
      </div>
    );
  };

  // ===== RENDER EXPERIENCE FIELD =====
  const renderExperienceField = (section) => {
    const expFieldLabels = {
      airline: 'Airline',
      position: 'Position',
      dates: 'Dates',
      aircraft: 'Aircraft',
      responsibilities: 'Responsibilities',
      company: 'Company',
      title: 'Title',
      startDate: 'Start Date',
      endDate: 'End Date',
      description: 'Description',
      institution: 'Institution',
      specialty: 'Specialty'
    };
    const value = formData[section.id] || [];
    
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label fw-medium mb-0">
            {section.label} {section.required && <span className="text-danger">*</span>}
          </label>
          <button
            type="button"
            onClick={() => handleArrayAdd(section.id)}
            className="btn btn-link btn-sm text-primary text-decoration-none d-flex align-items-center gap-1 p-0"
          >
            <Plus size={16} /> Add
          </button>
        </div>
        {value.map((item, idx) => (
          <div key={idx} className="border-top pt-3 mt-3">
            <div className="row g-3">
              {section.fields.map((field) => (
                <div key={field} className={field === 'responsibilities' || field === 'description' ? 'col-md-12' : 'col-md-6'}>
                  <input
                    value={item[field] || ''}
                    onChange={(e) => handleArrayUpdate(section.id, idx, field, e.target.value)}
                    placeholder={expFieldLabels[field] || field}
                    className="form-control"
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => handleArrayRemove(section.id, idx)}
              className="btn btn-link btn-sm text-danger text-decoration-none d-flex align-items-center gap-1 p-0 mt-2"
            >
              <Trash2 size={16} /> Remove
            </button>
          </div>
        ))}
        {value.length === 0 && (
          <span className="text-muted small">No entries added</span>
        )}
      </div>
    );
  };

  // ===== RENDER PROJECTS FIELD =====
  const renderProjectsField = (section) => {
    const projFieldLabels = {
      projectName: 'Project Name',
      description: 'Description',
      technologies: 'Technologies',
      outcome: 'Outcome',
      role: 'Role',
      client: 'Client',
      impact: 'Impact'
    };
    const value = formData[section.id] || [];
    
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label fw-medium mb-0">
            {section.label} {section.required && <span className="text-danger">*</span>}
          </label>
          <button
            type="button"
            onClick={() => handleArrayAdd(section.id)}
            className="btn btn-link btn-sm text-primary text-decoration-none d-flex align-items-center gap-1 p-0"
          >
            <Plus size={16} /> Add
          </button>
        </div>
        {value.map((item, idx) => (
          <div key={idx} className="border-top pt-3 mt-3">
            <div className="row g-3">
              {section.fields.map((field) => (
                <div key={field} className="col-12">
                  <input
                    value={item[field] || ''}
                    onChange={(e) => handleArrayUpdate(section.id, idx, field, e.target.value)}
                    placeholder={projFieldLabels[field] || field}
                    className="form-control"
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => handleArrayRemove(section.id, idx)}
              className="btn btn-link btn-sm text-danger text-decoration-none d-flex align-items-center gap-1 p-0 mt-2"
            >
              <Trash2 size={16} /> Remove
            </button>
          </div>
        ))}
        {value.length === 0 && (
          <span className="text-muted small">No projects added</span>
        )}
      </div>
    );
  };

  // ===== RENDER PUBLICATIONS FIELD =====
  const renderPublicationsField = (section) => {
    const pubFieldLabels = {
      title: 'Title',
      journal: 'Journal',
      year: 'Year',
      doi: 'DOI',
      arxivId: 'arXiv ID',
      impactFactor: 'Impact Factor'
    };
    const value = formData[section.id] || [];
    
    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label fw-medium mb-0">
            {section.label} {section.required && <span className="text-danger">*</span>}
          </label>
          <button
            type="button"
            onClick={() => handleArrayAdd(section.id)}
            className="btn btn-link btn-sm text-primary text-decoration-none d-flex align-items-center gap-1 p-0"
          >
            <Plus size={16} /> Add
          </button>
        </div>
        {value.map((item, idx) => (
          <div key={idx} className="border-top pt-3 mt-3">
            <div className="row g-3">
              {section.fields.map((field) => (
                <div key={field} className="col-md-6">
                  <input
                    value={item[field] || ''}
                    onChange={(e) => handleArrayUpdate(section.id, idx, field, e.target.value)}
                    placeholder={pubFieldLabels[field] || field}
                    className="form-control"
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => handleArrayRemove(section.id, idx)}
              className="btn btn-link btn-sm text-danger text-decoration-none d-flex align-items-center gap-1 p-0 mt-2"
            >
              <Trash2 size={16} /> Remove
            </button>
          </div>
        ))}
        {value.length === 0 && (
          <span className="text-muted small">No publications added</span>
        )}
      </div>
    );
  };

  // ===== RENDER SINGLE SECTION =====
  const renderSection = (section) => {
    const value = formData[section.id];

    switch (section.type) {
      case 'textarea':
        return (
          <div className="mb-4">
            <label className="form-label fw-medium mb-1">
              {section.label} {section.required && <span className="text-danger">*</span>}
            </label>
            <textarea
              name={section.id}
              value={value || ''}
              onChange={handleInputChange}
              rows={4}
              placeholder={section.placeholder || ''}
              className="form-control"
            />
          </div>
        );

      case 'text':
        return (
          <div className="mb-4">
            <label className="form-label fw-medium mb-1">
              {section.label} {section.required && <span className="text-danger">*</span>}
            </label>
            <input
              type="text"
              name={section.id}
              value={value || ''}
              onChange={handleInputChange}
              placeholder={section.placeholder || ''}
              className="form-control"
            />
          </div>
        );

      case 'flyingHours': {
        const fieldLabels = {
          totalHours: 'Total Hours',
          multiEngine: 'Multi-Engine Hours',
          instrument: 'Instrument Hours',
          night: 'Night Hours',
          crossCountry: 'Cross-Country Hours',
          turbine: 'Turbine Hours'
        };
        return (
          <div className="mb-4">
            <label className="form-label fw-medium mb-2">
              {section.label} {section.required && <span className="text-danger">*</span>}
            </label>
            <div className="row g-3">
              {section.fields.map((field) => (
                <div key={field} className="col-6 col-md-4">
                  <label className="form-label small text-muted mb-1">{fieldLabels[field] || field}</label>
                  <input
                    type="number"
                    value={value?.[field] || ''}
                    onChange={(e) => handleNestedInputChange(section.id, field, e.target.value)}
                    placeholder={fieldLabels[field] || field}
                    min="0"
                    step="1"
                    className="form-control"
                  />
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'skills':
        return renderSkillsField(section);

      case 'experience':
      case 'flight-experience':
        return renderExperienceField(section);

      case 'projects':
        return renderProjectsField(section);

      case 'publications':
        return renderPublicationsField(section);

      default:
        return renderStringArrayField(section);
    }
  };

  return (
    <div>
      {/* ===== PERSONAL INFORMATION ===== */}
      <div className="bg-white rounded-3 shadow-sm p-4 mb-4">
        <h3 className="h6 fw-semibold mb-3">Personal Information</h3>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label small fw-medium mb-1">Full Name *</label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="John Doe"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-medium mb-1">Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="john@example.com"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-medium mb-1">Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="+1 234 567 8900"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-medium mb-1">Location</label>
            <input
              type="text"
              name="address"
              value={formData.address || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="City, Country"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-medium mb-1">Professional Title</label>
            <input
              type="text"
              name="title"
              value={formData.title || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="e.g. Senior Software Engineer"
            />
          </div>
          <div className="col-12">
            <label className="form-label small fw-medium mb-1">Professional Summary</label>
            <textarea
              name="summary"
              value={formData.summary || ''}
              onChange={handleInputChange}
              rows={3}
              className="form-control"
              placeholder="Write a brief summary of your professional background..."
            />
          </div>
          <div className="col-12">
            <label className="form-label small fw-medium mb-1">Profile Photo</label>
            <div className="d-flex align-items-center gap-3">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                disabled={uploading}
                className="form-control"
              />
              {uploading && <span className="text-muted small">Uploading...</span>}
            </div>
            {formData.photo_url && (
              <img
                src={formData.photo_url}
                alt="Profile"
                className="mt-2 rounded-circle object-fit-cover"
                style={{ width: 64, height: 64 }}
              />
            )}
          </div>
        </div>
      </div>

      {/* ===== EDUCATION SECTION (Always shown) ===== */}
      <div className="bg-white rounded-3 shadow-sm p-4 mb-4">
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="fs-5">🎓</span>
          <h3 className="h6 fw-semibold mb-0">Education</h3>
          <span className="text-danger small">*</span>
        </div>
        {renderEducationField()}
      </div>

      {/* ===== CERTIFICATIONS SECTION (Always shown) ===== */}
      <div className="bg-white rounded-3 shadow-sm p-4 mb-4">
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="fs-5">📜</span>
          <h3 className="h6 fw-semibold mb-0">Certifications</h3>
        </div>
        {renderCertificationsField()}
      </div>

      {/* ===== LANGUAGES SECTION (Always shown) ===== */}
      <div className="bg-white rounded-3 shadow-sm p-4 mb-4">
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="fs-5">🌐</span>
          <h3 className="h6 fw-semibold mb-0">Languages</h3>
        </div>
        {renderLanguagesField()}
      </div>

      {/* ===== INDUSTRY-SPECIFIC SECTIONS ===== */}
      {sections.map((section) => {
        // Skip sections that are already rendered above
        const skipSections = ['summary', 'education', 'certifications', 'languages'];
        if (skipSections.includes(section.id)) return null;
        
        return (
          <div key={section.id} className="bg-white rounded-3 shadow-sm p-4 mb-4">
            <div className="d-flex align-items-center gap-2 mb-3">
              {section.icon && <span className="fs-5">{section.icon}</span>}
              <h3 className="h6 fw-semibold mb-0">{section.label}</h3>
              {section.required && <span className="text-danger small">*</span>}
            </div>
            {renderSection(section)}
          </div>
        );
      })}
    </div>
  );
};

export default ResumeForm;